import React, { useRef, useEffect, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Sparkles, ContactShadows, Float } from "@react-three/drei";
import ChargerModel from "./ChargerModel";

/**
 * Fixed full-screen 3D scene. Listens to global scroll and pointer state via window events.
 * Uses refs (not React state) so high-frequency pointer/scroll events don't trigger re-renders.
 */

// Static config — extracted to module scope to avoid recreating each render.
const CAMERA_CONFIG = { position: [0, 0, 5], fov: 45 };
const GL_CONFIG = { antialias: true, alpha: true, powerPreference: "high-performance" };
const DPR = [1, 2];

const LIGHTS = [
  { id: "key", position: [5, 4, 3], intensity: 3.5, color: "#ff9eeb", distance: 20 },
  { id: "fill", position: [-5, 2, 4], intensity: 2.5, color: "#a2e2ff", distance: 20 },
  { id: "rim", position: [0, -3, -3], intensity: 2, color: "#ffd074", distance: 15 },
  // Luces blancas dedicadas al producto para que se vea nítido sobre el fondo oscuro.
  { id: "product", position: [2.5, 2, 4], intensity: 6, color: "#ffffff", distance: 22 },
  { id: "product2", position: [-2.5, 1.5, 4], intensity: 4.5, color: "#eaf2ff", distance: 22 },
  { id: "product-top", position: [0, 4, 2], intensity: 3.5, color: "#ffffff", distance: 18 },
];

const SHADOW_POSITION = [0, -1.9, 0];
const FLOAT_PROPS = { speed: 1.3, rotationIntensity: 0.15, floatIntensity: 0.5 };

const SPARKLE_LAYERS = [
  { id: "pink", count: 120, scale: [8, 6, 6], size: 2.5, speed: 0.4, opacity: 0.7, color: "#ff9eeb" },
  { id: "cyan", count: 80, scale: [7, 5, 5], size: 1.8, speed: 0.3, opacity: 0.5, color: "#a2e2ff" },
  { id: "amber", count: 60, scale: [6, 4, 4], size: 1.5, speed: 0.25, opacity: 0.4, color: "#ffd074" },
];

const SPOT_POSITION = [0, 8, 0];

// Compute camera target position from normalized scroll (0..1).
function cameraTargetFromScroll(scroll) {
  // Cámara SIEMPRE centrada: el producto permanece en el centro, detrás del
  // contenido, mientras se hace scroll (solo un leve acercamiento para dar vida).
  // El movimiento/interés visual lo aporta el giro del modelo ligado al scroll.
  const s = Math.max(0, Math.min(scroll, 1));
  const targetZ = 5.0 - Math.sin(s * Math.PI) * 0.35;
  return [0, 0, targetZ];
}

// Wire up scroll + pointer to refs (no re-renders for 60fps input streams).
function useInputRefs() {
  const scrollRef = useRef(0);
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onScroll = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      scrollRef.current = docH > 0 ? window.scrollY / docH : 0;
    };
    const onPointer = (e) => {
      pointerRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointerRef.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onPointer);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onPointer);
    };
    // Refs are stable; one-time listener attachment is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scrollRef, pointerRef };
}

const Lights = () => (
  <>
    <ambientLight intensity={0.55} />
    {LIGHTS.map((l) => (
      <pointLight
        key={l.id}
        position={l.position}
        intensity={l.intensity}
        color={l.color}
        distance={l.distance}
      />
    ))}
    <spotLight position={SPOT_POSITION} angle={0.5} intensity={1.2} penumbra={1} color="#ffffff" />
  </>
);

const ParticleField = () => (
  <>
    {SPARKLE_LAYERS.map((s) => (
      <Sparkles
        key={s.id}
        count={s.count}
        scale={s.scale}
        size={s.size}
        speed={s.speed}
        opacity={s.opacity}
        color={s.color}
      />
    ))}
  </>
);

const CameraRig = ({ scrollRef }) => {
  useFrame(({ camera }) => {
    const [tx, ty, tz] = cameraTargetFromScroll(scrollRef.current);
    camera.position.x += (tx - camera.position.x) * 0.06;
    camera.position.y += (ty - camera.position.y) * 0.06;
    camera.position.z += (tz - camera.position.z) * 0.06;
    camera.lookAt(0, 0, 0);
  });
  return null;
};

const SceneContent = () => {
  const { scrollRef, pointerRef } = useInputRefs();

  return (
    <>
      <Lights />
      <CameraRig scrollRef={scrollRef} />

      {/* Producto 3D de fondo: el cargador FullStop, girando según el scroll. */}
      <Suspense fallback={null}>
        <Float {...FLOAT_PROPS}>
          <ChargerModel scrollRef={scrollRef} pointerRef={pointerRef} />
        </Float>
      </Suspense>

      <ParticleField />

      <ContactShadows position={SHADOW_POSITION} opacity={0.4} scale={9} blur={2.6} far={4} color="#a2e2ff" />

      <Environment preset="city" />
    </>
  );
};

const SCENE_STYLE = {
  background: "radial-gradient(ellipse at center, #0a0a14 0%, #050505 60%, #000 100%)",
};

const Scene3D = () => {
  return (
    <div
      data-testid="scene-3d-canvas"
      className="fixed inset-0 z-0 pointer-events-none"
      style={SCENE_STYLE}
    >
      <Canvas camera={CAMERA_CONFIG} gl={GL_CONFIG} dpr={DPR}>
        <SceneContent />
      </Canvas>
    </div>
  );
};

export default Scene3D;
